import { useCallback } from 'react';
import * as Yup from 'yup';
import axios from 'axios';
import Container from '@/components/Container';
import Form, {
  FormContent,
  FormFields,
  FormField,
  FormActions,
  FormSubmit,
  TextField,
  EmailField,
  TextAreaField,
  CountrySelectField,
} from '@/components/Form';
import { withAuthenticatedUser } from '@/components/ProtectedPage';
import { notifySuccess, notifyError } from '@/components/Toaster';
import getHSCookie from '@/lib/getHSCookie';

function getSubmitData(values, cookie = '', context = {}) {
  let fields = [];

  Object.keys(values).forEach((field) => {
    fields = [
      ...fields,
      {
        name: field,
        value: values[field],
      },
    ];
  });

  let contextFinal = {
    ...context,
  };

  if (cookie !== '' && cookie !== undefined) {
    contextFinal = {
      ...contextFinal,
      hutk: cookie,
    };
  }

  const now = new Date();

  return {
    submittedAt: now.valueOf(),
    fields,
    context: contextFinal,
  };
}

function submitFormHubSpot(
  formId,
  submitData,
  { setSubmitting, onSuccess, onError }
) {
  const formUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${formId}`;

  axios
    .post(formUrl, submitData)
    .then(() => {
      setSubmitting(false);
      onSuccess();
    })
    .catch((error) => {
      setSubmitting(false);
      onError(error);
    });
}

function submitForm(
  form,
  values,
  { setSubmitting, onSuccess, onError, cookie }
) {
  setSubmitting(true);

  const submitData = getSubmitData(values, getHSCookie(cookie), {
    pageUri: 'socios.aibes.org',
    pageName: 'Portal de socios',
  });

  // First, get the client IP address
  axios
    .get('https://api.ipify.org/')
    .then(({ data: ip }) => {
      submitFormHubSpot(
        form,
        {
          ...submitData,
          context: {
            ...submitData.context,
            ipAddress: ip,
          },
        },
        {
          setSubmitting,
          onSuccess,
          onError,
        }
      );
    })
    .catch(() => {
      submitFormHubSpot(form, submitData, {
        setSubmitting,
        onSuccess,
        onError,
      });
    });
}

const contactFormValidationSchema = Yup.object({
  firstName: Yup.string().required('Este campo es requerido'),
  lastName: Yup.string().required('Este campo es requerido'),
  email: Yup.string()
    .email('Debe incluir un correo electrónico válido')
    .required('Este campo es requerido'),
  phone: Yup.string().required('Este campo es requerido'),
  company: Yup.string().required('Este campo es requerido'),
  country: Yup.string().required('Este campo es requerido'),
  message: Yup.string().required('Este campo es requerido'),
});

function ContactPage({ user }) {
  const submitContactForm = useCallback(
    ({ values, setSubmitting, resetForm }) => {
      setSubmitting(true);

      const { firstName, lastName, ...hsFormValues } = values;

      submitForm(
        '7663692/a2179347-3705-49f7-a43e-961f18dd123e',
        {
          firstname: firstName,
          lastname: lastName,
          ...hsFormValues,
        },
        {
          setSubmitting,
          onSuccess: function () {
            resetForm();
            notifySuccess(
              'El formulario ha sido enviado. Gracias por ponerse en contacto con nosotros.'
            );
          },
          onError: function () {
            notifyError(
              'Ha ocurrido un error al enviar el formulario. Por favor, inténtelo nuevamente.'
            );
          },
        }
      );
    },
    []
  );

  return (
    <div className="w-full py-10 lg:py-20 relative overflow-hidden bg-gradient-to-t from-white to-gray-100">
      <Container className="space-y-10 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 xl:gap-20">
          <div className="lg:col-span-6 flex flex-col space-y-6 lg:space-y-10 text-lg lg:pt-20 xl:pt-32">
            <div className="flex flex-col space-y-4 lg:space-y-6">
              <h2 className="text-xl lg:text-3xl xl:text-5xl font-semibold lg:font-bold lg:tracking-tight">
                Contáctenos
              </h2>
              <div className="text-lg lg:text-xl text-gray-700">
                <p className="leading-relaxed">
                  Nuestro equipo estará encantado de responder a tus preguntas
                  sobre nuestro programa de socios. Completa el siguiente
                  formulario y nos pondremos en contacto lo antes posible.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="text-gray-600">
                Para consultas generales, incluidas oportunidades de asociación,
                envíe un correo electrónico a&nbsp;
                <a
                  className="font-semibold text-gray-900 hover:text-green-500"
                  href="mailto:info@aibes.org"
                >
                  info@aibes.org
                </a>
                .
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white rounded-lg lg:rounded-xl shadow-lg lg:shadow-xl ring-1 ring-black ring-opacity-10">
              <div className="px-8 py-10 lg:px-10 lg:py-12">
                <Form
                  initialValues={{
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    email: user?.email,
                    phone: '',
                    company: '',
                    country: '',
                    message: '',
                  }}
                  handleSubmit={submitContactForm}
                  validationSchema={contactFormValidationSchema}
                  validateOnMount={true}
                  enableReinitialize
                >
                  {() => {
                    return (
                      <FormContent>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6">
                          <div className="md:col-span-6">
                            <FormFields>
                              <FormField label="Nombres" fieldName="firstName">
                                <TextField name="firstName" />
                              </FormField>
                            </FormFields>
                          </div>
                          <div className="md:col-span-6">
                            <FormFields>
                              <FormField label="Apellidos" fieldName="lastName">
                                <TextField name="lastName" />
                              </FormField>
                            </FormFields>
                          </div>
                          <div className="md:col-span-12">
                            <FormFields>
                              <FormField
                                label="Correo electrónico"
                                fieldName="email"
                              >
                                <EmailField name="email" />
                              </FormField>
                              <FormField label="País" fieldName="country">
                                <CountrySelectField name="country" />
                              </FormField>
                            </FormFields>
                          </div>
                          <div className="md:col-span-6">
                            <FormFields>
                              <FormField label="Empresa" fieldName="company">
                                <TextField name="company" />
                              </FormField>
                            </FormFields>
                          </div>
                          <div className="md:col-span-6">
                            <FormFields>
                              <FormField
                                label="Número de teléfono"
                                fieldName="phone"
                              >
                                <TextField name="phone" />
                              </FormField>
                            </FormFields>
                          </div>
                          <div className="md:col-span-12">
                            <FormFields>
                              <FormField label="Mensaje" fieldName="message">
                                <TextAreaField name="message" />
                              </FormField>
                            </FormFields>
                          </div>
                        </div>
                        <div className="text-xs leading-relaxed text-gray-500">
                          AIBES necesita la información que proporcionó para
                          comunicarse con usted acerca de nuestros productos y
                          servicios. Puede darse de baja de estas comunicaciones
                          en cualquier momento. Para obtener información sobre
                          cómo cancelar la suscripción, así como nuestras
                          prácticas de privacidad y el compromiso de proteger su
                          privacidad, consulte nuestra&nbsp;
                          <a
                            className="font-medium"
                            href="https://www.aibes.org/legal/privacidad"
                          >
                            Política de privacidad
                          </a>
                          .
                        </div>
                        <FormActions>
                          <FormSubmit>Enviar mensaje</FormSubmit>
                        </FormActions>
                      </FormContent>
                    );
                  }}
                </Form>
              </div>
            </div>
          </div>
        </div>
      </Container>
      <div
        className="absolute inset-0 transform -skew-y-12 bg-gray-100 bg-gradient-to-tl from-green-100 via-gray-100 to-white flex flex-col items-end"
        style={{ height: '1100px', top: '-500px' }}
      >
        <div className="h-24 w-3/12 bg-gradient-to-l from-green-500 to-green-500 z-20 absolute bottom-0 right-0" />
        <div className="h-20 w-2/12 bg-gradient-to-l from-green-500 to-transparent z-20 absolute bottom-0 right-0 mb-24" />
        <div className="h-14 w-3/12 bg-green-500 z-20 absolute bottom-0 left-0 -mb-14" />
        <div className="h-10 w-2/12 border-t border-r border-green-500 z-20 absolute bottom-0 left-0" />
      </div>
    </div>
  );
}

export default withAuthenticatedUser(ContactPage, {
  doNotRedirectToLogin: true,
});
